import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import type { Named } from './shell'

/**
 * Fisa aparatului NOU - randul desfacut din Unassigned (v4/api,
 * App\Inventoree\Unassigned\Card\*). Numele campurilor sunt cele scrise de
 * server; forma e inghetata in `src/inventoree/__fixtures__/unassignedCard*.json`,
 * citita si de proba din browser, si de cea din PHP.
 */

export type UnassignedPane = 'details' | 'debug' | 'assign'

export interface UnassignedCardBand {
  panes: UnassignedPane[]
}

export interface UnassignedAddress {
  ip: string
  prefix: number | null
  cidr: string | null
}

/** Coloana din stanga - `Overview::unassignedDetails`. */
export interface UnassignedDetails {
  device: { id: number }
  name: string
  type: string
  virtual: boolean
  os: string
  osFamily: string | null
  osFamilyName: string
  osVersion: string
  model: string
  serial: string
  /** `sLastIPIntern` - goala pe o masina abia inregistrata. */
  internalIp: string
  addresses: UnassignedAddress[]
  /** Adresa cu care a vazut-o scanarea, cand nici agentul n-a spus una. */
  scannedIp: string
  externalIp: string
  mac: string
  /** "Seen from" - acelasi drum ca propunerea din Assign. */
  location: { name: string; source: string; matched: string } | null
  agentVersion: string
  firstRegistered: string
  lastCommunication: string
  loggedOn: string
  /** Cate masini cu agent poarta acelasi nume. */
  matched: number
  owner: string
}

/** Coloana din mijloc - `Overview::deviceDebugSettings`. */
export interface UnassignedDebug {
  device: { id: number }
  autoUpdate: boolean
  beta: boolean
  adRelay: boolean
  isLinux: boolean
}

/** Coloana din dreapta - `Overview::deviceAssignSettings`. */
export interface UnassignedAssign {
  device: { id: number }
  ownerId: number
  areaId: number
  officeId: number
  groupId: number
  classified: string
  owners: { id: number; text: string }[]
  areas: Named[]
  offices: Named[]
  groups: Named[]
  suggestedOffice: { id: number; name: string; source: string; matched: string } | null
  learnExternal: string
  learnInternal: string
  learnAssumed: boolean
}

const BASE = '/inventoree/unassigned'

export function unassignedCardQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'unassigned', 'card', id],
    queryFn: () => apiGet<UnassignedCardBand>(`${BASE}/${id}/card`),
  })
}

export function unassignedDetailsQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'unassigned', 'card', id, 'details'],
    queryFn: () => apiGet<UnassignedDetails>(`${BASE}/${id}/details`),
  })
}

export function unassignedDebugQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'unassigned', 'card', id, 'debug'],
    queryFn: () => apiGet<UnassignedDebug>(`${BASE}/${id}/debug`),
  })
}

export function unassignedAssignQuery(id: number) {
  return queryOptions({
    queryKey: ['inventoree', 'unassigned', 'card', id, 'assign'],
    queryFn: () => apiGet<UnassignedAssign>(`${BASE}/${id}/assign`),
  })
}

/** Save-ul unic al fisei - `Overview::updateSettings` (+ `ad_relay`). */
export function saveUnassignedCard(id: number, fields: Record<string, string>) {
  return apiPost<{ ok: boolean; owner: number }>(`${BASE}/${id}/assign`, fields)
}

/** `Overview::retrieveLogsRequest` din coloana de mijloc - comanda LOG cu adresa. */
export function retrieveUnassignedLogs(id: number, email: string) {
  return apiPost<{ ok: boolean }>(`${BASE}/${id}/retrieve-logs`, { logs_email: email })
}

/** `Overview::quickAddOffice` - sediu nou pe loc, pe firma aleasa. */
export function quickAddOffice(name: string) {
  return apiPost<{ id: number; text: string }>(`${BASE}/quick-add-office`, { office_name: name })
}

/** `Overview::rememberOfficeLocation` - leaga adresele bifate de sediul ales. */
export function rememberOfficeLocation(office: number, external: string, internal: string) {
  return apiPost<{ done: string[]; failed: string[] }>(`${BASE}/remember-location`, { office, external, internal })
}
